import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import CourtesyVoucherUpsertModal from "./CourtesyVoucherUpsertModal";

function fmtDate(d) {
  if (!d) return "-";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "-";
  return x.toLocaleDateString();
}

export default function CourtesyVoucherAdminList() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const [sortBy, setSortBy] = useState("created_at");
  const [order, setOrder] = useState("DESC");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const [deactOpen, setDeactOpen] = useState(false);
  const [deactRow, setDeactRow] = useState(null);
  const [deactReason, setDeactReason] = useState("");
  const [deactLoading, setDeactLoading] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchList = async (opts = {}) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/courtesy-vouchers", {
        params: {
          page: opts.page ?? page,
          limit,
          search: (opts.search ?? search) || undefined,
          status: (opts.status ?? status) || undefined,
          sortBy,
          order,
        },
      });

      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: 1, limit, totalPages: 1 });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load vouchers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ page: 1 });
  }, []);

  const onApply = (e) => {
    e.preventDefault();
    setPage(1);
    fetchList({ page: 1 });
  };

  const onCreate = async (payload) => {
    setCreateLoading(true);
    try {
      const res = await api.post("/admin/courtesy-vouchers", payload);
      await fetchList({ page: 1 });
      setPage(1);
      return res;
    } finally {
      setCreateLoading(false);
    }
  };

  const openView = (r) => {
    setViewRow(r);
    setViewOpen(true);
  };

  const openDeactivate = (r) => {
    setDeactRow(r);
    setDeactReason("");
    setDeactOpen(true);
  };

  const doDeactivate = async () => {
    if (!deactReason.trim()) return toast.error("Reason required");
    setDeactLoading(true);
    try {
      await api.patch(`/admin/courtesy-vouchers/${deactRow.voucher_id}/deactivate`, { reason: deactReason.trim() });
      toast.success("Voucher deactivated");
      setDeactOpen(false);
      setDeactRow(null);
      await fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Deactivate failed");
    } finally {
      setDeactLoading(false);
    }
  };

  const openDelete = (r) => {
    setDeleteRow(r);
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/admin/courtesy-vouchers/${deleteRow.voucher_id}`);
      toast.success("Voucher deleted");
      setDeleteOpen(false);
      setDeleteRow(null);
      await fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  const gotoPage = (p) => {
    const next = Math.min(Math.max(1, p), meta.totalPages || 1);
    setPage(next);
    fetchList({ page: next });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Courtesy Vouchers</h1>
          <p className="text-sm text-gray-600 mt-1">Create voucher for a specific user • valid 365 days.</p>
        </div>

        <button className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 self-start" onClick={() => setCreateOpen(true)}>
          + Create Voucher
        </button>
      </div>

      <CourtesyVoucherUpsertModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={onCreate}
        loading={createLoading}
      />

      <form onSubmit={onApply} className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4">
        <div className="min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Search by voucher code / user id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="min-w-[200px]">
          <label className="text-sm font-semibold">Status</label>
          <select className="mt-2 w-full border rounded-xl px-3 py-2" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        {/* <div className="min-w-[220px]">
          <label className="text-sm font-semibold">Sort By</label>
          <select className="mt-2 w-full border rounded-xl px-3 py-2" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="created_at">created_at</option>
            <option value="valid_till">valid_till</option>
            <option value="voucher_value">voucher_value</option>
            <option value="status">status</option>
          </select>
        </div> */}

        {/* <div className="min-w-[140px]">
          <label className="text-sm font-semibold">Order</label>
          <select className="mt-2 w-full border rounded-xl px-3 py-2" value={order} onChange={(e) => setOrder(e.target.value)}>
            <option value="DESC">DESC</option>
            <option value="ASC">ASC</option>
          </select>
        </div> */}

        <button type="submit" className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90">
          Apply
        </button>
      </form>

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold">Voucher List</div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No vouchers found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Valid Till</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="border-t">
              {rows.map((r) => (
                <tr key={r.voucher_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{r.voucher_code}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">
                      {r.user ? `${r.user.first_name || ""} ${r.user.last_name || ""}`.trim() || r.user.email : "-"}
                    </div>
                    <div className="text-xs text-gray-600">{r.user_id}</div>
                  </td>
                  <td className="px-4 py-3">₹{r.voucher_value}</td>
                  <td className="px-4 py-3">{fmtDate(r.valid_till)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${Number(r.status) === 1 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                      {Number(r.status) === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50" onClick={() => openView(r)}>
                        View
                      </button>
                      {Number(r.status) === 1 ? (
                        <button className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50" onClick={() => openDeactivate(r)}>
                          Deactivate
                        </button>
                      ) : null}
                      <button className="rounded-lg border border-red-200 text-red-600 px-3 py-1 text-xs font-semibold hover:bg-red-50" onClick={() => openDelete(r)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3 border-t bg-white text-sm">
          <div>
            Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: {meta.total})
          </div>
          <div className="flex gap-2">
            <button className="rounded-lg border px-3 py-1 hover:bg-gray-50 disabled:opacity-50" disabled={meta.page <= 1} onClick={() => gotoPage(meta.page - 1)}>
              Prev
            </button>
            <button className="rounded-lg border px-3 py-1 hover:bg-gray-50 disabled:opacity-50" disabled={meta.page >= meta.totalPages} onClick={() => gotoPage(meta.page + 1)}>
              Next
            </button>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {viewOpen ? (
        <div className="fixed inset-0 z-50 bg-black/30 grid place-items-center p-4">
          <div className="w-full max-w-xl bg-white rounded-2xl border p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="text-lg font-bold">Voucher Details</div>
              <button onClick={() => setViewOpen(false)} className="text-sm px-3 py-1 rounded-lg hover:bg-gray-100">
                Close
              </button>
            </div>

            <div className="mt-4 text-sm space-y-2">
              <div><b>Code:</b> {viewRow.voucher_code}</div>
              <div><b>User ID:</b> {viewRow.user_id}</div>
              <div><b>Amount:</b> ₹{viewRow.voucher_value}</div>
              <div><b>Valid Till:</b> {fmtDate(viewRow.valid_till)}</div>
              <div><b>Trip:</b> {viewRow.trip_type}</div>
              <div><b>Passenger:</b> {viewRow.passenger_type}</div>
              <div><b>Max Usage:</b> {viewRow.max_usage}</div>
              <div><b>Used Count:</b> {viewRow.used_count}</div>
              <div><b>Status:</b> {Number(viewRow.status) === 1 ? "Active" : "Inactive"}</div>
              <div><b>Notes:</b> {viewRow.notes || "-"}</div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Deactivate Modal */}
      {deactOpen ? (
        <div className="fixed inset-0 z-50 bg-black/30 grid place-items-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl border p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="text-lg font-bold">Deactivate Voucher</div>
              <button onClick={() => setDeactOpen(false)} className="text-sm px-3 py-1 rounded-lg hover:bg-gray-100">
                Close
              </button>
            </div>

            <div className="mt-4">
              <label className="text-sm font-semibold">Reason</label>
              <textarea className="mt-2 w-full border rounded-xl px-3 py-2 min-h-[90px]" value={deactReason} onChange={(e) => setDeactReason(e.target.value)} />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50" onClick={() => setDeactOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold disabled:opacity-60"
                disabled={deactLoading}
                onClick={doDeactivate}
              >
                {deactLoading ? "Saving..." : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Delete Modal */}
      {deleteOpen ? (
        <div className="fixed inset-0 z-50 bg-black/30 grid place-items-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl border p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="text-lg font-bold">Delete Voucher</div>
              <button onClick={() => setDeleteOpen(false)} className="text-sm px-3 py-1 rounded-lg hover:bg-gray-100">
                Close
              </button>
            </div>

            <p className="mt-4 text-sm text-gray-700">
              This will permanently delete voucher <b>{deleteRow?.voucher_code}</b>. This cannot be undone.
            </p>

            <div className="flex justify-end gap-2 pt-4">
              <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50" onClick={() => setDeleteOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-red-600 px-4 py-2 text-white text-sm font-semibold disabled:opacity-60"
                disabled={deleteLoading}
                onClick={doDelete}
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
